import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

/**
 * Top-level shell for the SDS module. Renders a sub-navigation strip
 * (Chemicals list / Exposure Register) above the router outlet so users
 * can switch between the document grid and the cross-document register
 * without leaving the module.
 *
 * Routing here is **relative** because the module is mounted under
 * `/app/business/{id}/safety-data-sheets`, `/app/portal/{id}/...`, and
 * `/app/admin/{id}/...`. Absolute routerLinks would only resolve under one
 * of those prefixes; relative ones work everywhere. Portal view shows an
 * availability notice because the SDS API requires a business context.
 */
@Component({
  template: `
    @if (isPortalView) {
      <section class="p-4" aria-labelledby="sds-portal-title">
        <h2 id="sds-portal-title" [translate]="'CHEMICALS'"></h2>
        <p role="status" [translate]="'SDS_PORTAL_UNAVAILABLE'"></p>
      </section>
    } @else {
      <nav class="sds-subnav">
        <a
          class="sds-subnav-link"
          [routerLink]="['list']"
          [relativeTo]="route"
          routerLinkActive="active"
          [class.active]="isChemicalRoute"
        >
          <i class="pi pi-database"></i>
          <span [translate]="'CHEMICALS'"></span>
        </a>
        <a
          class="sds-subnav-link"
          [routerLink]="['substitution-assessment']"
          [relativeTo]="route"
          routerLinkActive="active"
        >
          <i class="pi pi-sync"></i>
          <span [translate]="'SDS_SUBST_TITLE'"></span>
        </a>
        <a
          class="sds-subnav-link"
          [routerLink]="['exposure-register']"
          [relativeTo]="route"
          routerLinkActive="active"
        >
          <i class="pi pi-users"></i>
          <span [translate]="'SDS_EXPOSURE_REGISTER'"></span>
        </a>
        <a
          class="sds-subnav-link"
          [routerLink]="['archived']"
          [relativeTo]="route"
          routerLinkActive="active"
        >
          <i class="pi pi-inbox"></i>
          <span [translate]="'SDS_ARCHIVED_TITLE'"></span>
        </a>
      </nav>
      <router-outlet></router-outlet>
    }
  `,
  styles: [
    `
      .sds-subnav {
        display: flex;
        gap: 4px;
        overflow-x: auto;
        border-bottom: 1px solid #e5e7eb;
        margin: 0 0 16px;
        padding: 0;
      }
      .sds-subnav-link {
        display: inline-flex;
        flex-shrink: 0;
        white-space: nowrap;
        align-items: center;
        gap: 8px;
        padding: 10px 16px;
        font-weight: 500;
        color: #4b5563;
        text-decoration: none;
        border-bottom: 2px solid transparent;
        transition:
          color 0.15s ease,
          border-color 0.15s ease;
        cursor: pointer;
      }
      .sds-subnav-link:hover {
        color: #1f2937;
      }
      .sds-subnav-link.active {
        color: var(--p-primary-color);
        border-bottom-color: var(--p-primary-color);
      }
      .sds-subnav-link i {
        font-size: 14px;
      }
      @media (max-width: 480px) {
        .sds-subnav-link {
          padding: 8px 10px;
          font-size: 13px;
        }

        .sds-subnav-link i {
          font-size: 16px;
        }
      }
    `],
  standalone: false
})
export class SdsComponent {
  constructor(public readonly route: ActivatedRoute) {}

  // Use the active view, not the user's role: portal users can also open a business.
  // Omitting the outlet prevents all business-only child screens from making requests.
  get isPortalView(): boolean {
    return this.route.snapshot.pathFromRoot.some(
      // The root snapshot has no routeConfig, despite linting without strictNullChecks.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      ancestor => ancestor.routeConfig?.path === 'portal/:id'
    );
  }

  /**
   * Highlight "Chemicals" for `/list`, `/new`, `/detail/:id` — anything
   * that operates on a single document. Exposure Register has its own
   * exact match via routerLinkActive.
   */
  get isChemicalRoute(): boolean {
    const path = window.location.pathname;
    return ( path.endsWith('/safety-data-sheets') ||
      path.includes('/safety-data-sheets/list') ||
      path.includes('/safety-data-sheets/new') ||
      path.includes('/safety-data-sheets/detail'));
  }
}
